import type { TracedFace, TracedObstruction, RoofModel, RoofFace, Obstruction } from '../types';

// ============================================================
// ROOF EXTRUSION — Convert traced 2D polygons into 3D roof faces
// Takes the user's traced faces (with pitch) and generates a
// RoofModel that the SceneManager can render
// ============================================================

/**
 * Calculate azimuth (orientation in degrees from north) from a polygon.
 * We approximate this by finding the dominant edge direction.
 * For a simple gable roof traced as a rectangle, the long edges
 * define the ridge line direction, and the azimuth is perpendicular.
 *
 * Since our satellite imagery is north-up, the Y axis = north,
 * X axis = east. Azimuth is measured clockwise from north.
 */
function estimateAzimuth(points: [number, number][]): number {
  if (points.length < 2) return 180; // default south

  // Find the longest edge of the polygon
  let maxLen = 0;
  let bestAngle = 0;

  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > maxLen) {
      maxLen = len;
      // Angle of this edge, measured clockwise from north (Y axis)
      // atan2(dx, dy) gives angle from north, east = 90
      bestAngle = (Math.atan2(dx, dy) * 180) / Math.PI;
    }
  }

  // Normalize to 0-360
  bestAngle = ((bestAngle % 360) + 360) % 360;

  // The face azimuth is perpendicular to the ridge — pick the
  // direction that's closest to south (180°) for panel placement
  const perp1 = (bestAngle + 90) % 360;
  const perp2 = (bestAngle - 90 + 360) % 360;

  // Choose the one closer to south
  const dist1 = Math.min(Math.abs(perp1 - 180), 360 - Math.abs(perp1 - 180));
  const dist2 = Math.min(Math.abs(perp2 - 180), 360 - Math.abs(perp2 - 180));

  return dist1 < dist2 ? perp1 : perp2;
}

/**
 * Calculate polygon area using the shoelace formula.
 * Returns area in square feet.
 */
function polygonArea(points: [number, number][]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i][0] * points[j][1];
    area -= points[j][0] * points[i][1];
  }
  return Math.abs(area) / 2;
}

/**
 * Calculate a rough solar access percentage based on azimuth and pitch.
 * South-facing = best, north = worst.
 * This is a simplified heuristic — real shade analysis comes later.
 */
function estimateSolarAccess(azimuth: number, pitch: number): number {
  // Perfect south = 180°
  const southDiff = Math.min(
    Math.abs(azimuth - 180),
    360 - Math.abs(azimuth - 180)
  );

  // South-facing (0° diff) → 90% access
  // East/West (90° diff) → 72%
  // North (180° diff) → 45%
  let access = 90 - (southDiff / 180) * 45;

  // Optimal pitch is ~25-30° for most US latitudes
  // Penalize very flat or very steep
  const pitchDiff = Math.abs(pitch - 25);
  access -= pitchDiff * 0.3;

  // Clamp to 30-95
  return Math.max(30, Math.min(95, Math.round(access)));
}

/**
 * Convert traced faces and obstructions into a RoofModel.
 * Each traced face becomes a RoofFace with computed azimuth, area, and solar access.
 */
export function extrudeTracedRoof(
  tracedFaces: TracedFace[],
  tracedObstructions: TracedObstruction[] = []
): RoofModel {
  const faces: RoofFace[] = tracedFaces.map((tf, idx) => {
    const azimuth = estimateAzimuth(tf.points);
    const area = polygonArea(tf.points) / Math.cos((tf.pitch * Math.PI) / 180);

    return {
      id: tf.id,
      label: tf.label || `Face ${idx + 1}`,
      azimuth,
      pitch: tf.pitch,
      vertices: tf.points,
      height: tf.height,
      area_sqft: area,
      solar_access: estimateSolarAccess(azimuth, tf.pitch),
    };
  });

  // Calculate overall roof dimensions
  const allPoints = tracedFaces.flatMap(f => f.points);
  const xs = allPoints.map(p => p[0]);
  const ys = allPoints.map(p => p[1]);
  const roofWidth = xs.length > 0 ? Math.max(...xs) - Math.min(...xs) : 40;
  const roofDepth = ys.length > 0 ? Math.max(...ys) - Math.min(...ys) : 30;
  const roofHeight = tracedFaces.length > 0
    ? Math.max(...tracedFaces.map(f => f.height))
    : 18;
  const avgPitch = tracedFaces.length > 0
    ? tracedFaces.reduce((s, f) => s + f.pitch, 0) / tracedFaces.length
    : 25;

  // Convert traced obstructions to Obstruction entities
  const obstructions: Obstruction[] = tracedObstructions.map(to => ({
    id: to.id,
    type: to.type,
    position: to.position,
    width: to.width,
    height: to.height,
  }));

  return {
    faces,
    obstructions,
    roofWidth,
    roofDepth,
    roofHeight,
    pitchDeg: avgPitch,
    source: 'traced',
  };
}

/**
 * Generate a unique ID for a traced face.
 */
export function generateFaceId(): string {
  return `face_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
}

/**
 * Generate a unique ID for a traced obstruction.
 */
export function generateObstructionId(): string {
  return `obs_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
}
